import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { type SyncResult, SyncResultList } from './sync-results'

// Edits a customer's details. Saving writes our database, then the backend
// updates the copies in Chargebee, Brevo and the customer's own Nextcloud user
// and reports each one — shown here after the save, so the admin sees exactly
// what changed where (and what to fix by hand if one failed).

interface EditableUser {
	id: string
	name: string
	email: string
	company: string | null
	phone: string | null
	addressFirstName: string | null
	addressLastName: string | null
	addressLine1: string | null
	addressLine2: string | null
	addressCity: string | null
	addressState: string | null
	addressZip: string | null
	addressCountry: string | null
}

interface EditResponse {
	changed: string[]
	results: SyncResult[]
}

const ADDRESS_KEYS = ['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip', 'country'] as const

export function EditUserDialog({
	user,
	onClose,
	onSaved,
}: {
	user: EditableUser
	onClose: () => void
	onSaved: () => void
}) {
	// Mirrors the backend rule: an address can be replaced but not removed.
	const hadAddress = Boolean(user.addressLine1 && user.addressCity && user.addressZip && user.addressCountry)
	const [form, setForm] = useState({
		name: user.name ?? '',
		email: user.email,
		company: user.company ?? '',
		phone: user.phone ?? '',
		firstName: user.addressFirstName ?? '',
		lastName: user.addressLastName ?? '',
		line1: user.addressLine1 ?? '',
		line2: user.addressLine2 ?? '',
		city: user.addressCity ?? '',
		state: user.addressState ?? '',
		zip: user.addressZip ?? '',
		country: user.addressCountry ?? '',
	})
	const [result, setResult] = useState<EditResponse | null>(null)

	const addressTouched = hadAddress || ADDRESS_KEYS.some((k) => form[k].trim() !== '')
	const emailChanged = form.email.trim().toLowerCase() !== user.email

	const save = useMutation({
		mutationFn: () =>
			apiFetch<EditResponse>(`/api/admin/users/${user.id}`, {
				method: 'PATCH',
				body: {
					name: form.name,
					email: form.email,
					company: form.company,
					phone: form.phone,
					address: addressTouched
						? {
								firstName: form.firstName,
								lastName: form.lastName,
								line1: form.line1,
								line2: form.line2,
								city: form.city,
								state: form.state,
								zip: form.zip,
								country: form.country,
							}
						: null,
				},
			}),
		onSuccess: (data) => {
			onSaved()
			setResult(data)
		},
	})

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !save.isPending && onClose()
		document.addEventListener('keydown', onKey)
		document.body.style.overflow = 'hidden'
		return () => {
			document.removeEventListener('keydown', onKey)
			document.body.style.overflow = ''
		}
	}, [onClose, save.isPending])

	function set(key: keyof typeof form) {
		return (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }))
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<button
				type="button"
				aria-hidden
				tabIndex={-1}
				className="absolute inset-0 bg-black/40"
				onClick={() => !save.isPending && onClose()}
			/>
			{result ? (
				<div role="dialog" aria-modal className="card relative z-10 w-full max-w-lg p-5">
					<h2 className="font-display text-lg font-semibold">
						{result.changed.length ? 'Changes saved' : 'Nothing changed'}
					</h2>
					{result.changed.length > 0 && (
						<>
							<p className="mt-1 text-sm text-ink-soft">
								Saved {result.changed.join(', ')} in CBB.
								{result.results.length > 0 && ' Other systems:'}
							</p>
							{result.results.length > 0 && <SyncResultList results={result.results} />}
							{result.results.some((r) => r.status === 'failed') && (
								<p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
									The change is saved in CBB, but at least one system was not updated. Fix it there by hand or save again
									to retry.
								</p>
							)}
						</>
					)}
					<div className="mt-5 flex justify-end">
						<button type="button" className="btn-primary" onClick={onClose}>
							Done
						</button>
					</div>
				</div>
			) : (
				<form
					role="dialog"
					aria-modal
					className="card relative z-10 flex max-h-full w-full max-w-lg flex-col"
					onSubmit={(e) => {
						e.preventDefault()
						save.mutate()
					}}
				>
					<h2 className="border-b border-line px-5 py-4 font-display text-lg font-semibold">Edit user</h2>

					<div className="overflow-y-auto px-5 py-4">
						<div className="grid gap-3 sm:grid-cols-2">
							<TextField id="eu-name" label="Name" value={form.name} onChange={set('name')} />
							<TextField id="eu-email" label="Email" type="email" value={form.email} onChange={set('email')} required />
							<TextField id="eu-company" label="Company" value={form.company} onChange={set('company')} />
							<TextField id="eu-phone" label="Phone" type="tel" value={form.phone} onChange={set('phone')} />
						</div>

						{emailChanged && (
							<p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
								The customer will sign in with the new address from now on. Chargebee, Brevo and their own Nextcloud user
								are updated too.
							</p>
						)}

						<h3 className="mb-2 mt-5 text-sm font-semibold">Address</h3>
						{!hadAddress && (
							<p className="mb-3 text-xs text-ink-soft">No address on file. Leave empty, or fill in all required fields.</p>
						)}
						<div className="grid gap-3 sm:grid-cols-2">
							<TextField id="eu-first" label="First name" value={form.firstName} onChange={set('firstName')} required={addressTouched} />
							<TextField id="eu-last" label="Last name" value={form.lastName} onChange={set('lastName')} required={addressTouched} />
							<div className="sm:col-span-2">
								<TextField id="eu-line1" label="Address line 1" value={form.line1} onChange={set('line1')} required={addressTouched} />
							</div>
							<div className="sm:col-span-2">
								<TextField id="eu-line2" label="Address line 2" value={form.line2} onChange={set('line2')} />
							</div>
							<TextField id="eu-zip" label="Postcode" value={form.zip} onChange={set('zip')} required={addressTouched} />
							<TextField id="eu-city" label="City" value={form.city} onChange={set('city')} required={addressTouched} />
							<TextField id="eu-state" label="State / region" value={form.state} onChange={set('state')} />
							<TextField
								id="eu-country"
								label="Country (2-letter code)"
								value={form.country}
								onChange={set('country')}
								required={addressTouched}
								placeholder="DE"
								maxLength={2}
								pattern="[A-Za-z]{2}"
								title="Two-letter country code, e.g. DE"
							/>
						</div>
						{hadAddress && (
							<p className="mt-2 text-xs text-ink-soft">
								Billing address follows this one unless the customer set a separate billing address.
							</p>
						)}

						{save.error && (
							<p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(save.error as Error).message}</p>
						)}
					</div>

					<div className="flex justify-end gap-2 border-t border-line px-5 py-4">
						<button type="button" className="btn-secondary" onClick={onClose} disabled={save.isPending}>
							Cancel
						</button>
						<button type="submit" className="btn-primary" disabled={save.isPending}>
							{save.isPending ? 'Saving…' : 'Save changes'}
						</button>
					</div>
				</form>
			)}
		</div>
	)
}

function TextField({
	id,
	label,
	required,
	...input
}: { id: string; label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
	return (
		<div>
			<label className="label" htmlFor={id}>
				{label}
				{required && <span className="text-red-600"> *</span>}
			</label>
			<input id={id} className="input" required={required} {...input} />
		</div>
	)
}
